import { OrdersApiService } from '@/common/services/orders.api.service';
import { OrderItemsApiService } from '@/common/services/order-items.api.service';
import { OrderCustomerAccountsApiService } from '@/common/services/order-customer-accounts.api.service';
import { OrdersSearchingRemainingApiService } from '@/common/services/orders-searching-remaining.api.service';
import { OrdersPackagingRemainingApiService } from '@/common/services/orders-packaging-remaining.api.service';
import { OrderModel } from '@/common/models/order.model';
import { OrderItemModel } from '@/common/models/order-item.model';
import REQUEST_STATUS from '../../common/enums/requestStatus';
import ORDER_STATE from '../../common/enums/orderState';

export default {
  namespaced: true,

  state: {
    status: REQUEST_STATUS.LOADING,
    total: 0,
    remaining: 0,
    lastOrderChange: null,
    items: [],
    allItems: [],
  },

  getters: {
    getAllOrders: state => state.items,
    getAllItems: state => state.allItems,
    getStatus: state => state.status,
    getTotal: state => state.total,
    getRemaining: state => state.remaining,
    getOrder: state => id => state.items.find(item => item.id === id),
    getLastChange: state => state.lastOrderChange,
  },

  mutations: {
    SET_ITEMS(state, data) {
      state.items = data;
    },
    SET_ALL_ITEMS(state, data) {
      state.allItems = state.allItems.concat(data.orderItems);
      state.total = state.allItems.length;
    },
    SET_ITEM(state, data) {
      const arrayIndex = state.items.findIndex(item => item.id === data.id);

      if (arrayIndex === -1) {
        state.items.push(data);
      } else {
        this.$app.$set(state.items, arrayIndex, data);
      }
    },
    SET_TOTAL(state, data) {
      state.total = data;
    },
    SET_REMAINING(state, data) {
      state.remaining = data;
    },
    SET_ORDER_ITEMS(state, payload) {
      const order = state.items.find(item => item.id === payload.orderId);
      if (order) {
        order.extendOrder({ [OrderModel.SUB_ITEMS]: payload.items });
      }
    },
    SET_ORDER_ADDRESSES(state, payload) {
      const order = state.items.find(item => item.id === payload.orderId);
      order.extendOrder({ [OrderModel.SUB_ADDRESSES]: payload.items });
    },
    SET_ORDER_CUSTOMER_ACCOUNTS(state, payload) {
      const order = state.items.find(item => item.id === payload.orderId);
      order.extendOrder({ [OrderModel.SUB_CUSTOMER_ACCOUNTS]: payload.items });
    },
    SET_LAST_ORDER_CHANGE(state, payload) {
      state.lastOrderChange = payload;
    },
    FETCH_REQUEST(state) {
      state.status = REQUEST_STATUS.LOADING;
    },
    FETCH_SUCCESS(state) {
      state.status = REQUEST_STATUS.SUCCESS;
    },
    FETCH_FAIL(state) {
      state.status = REQUEST_STATUS.FAIL;
    },
    CLEAR_ITEMS(state) {
      state.allItems = [];
    },
  },

  actions: {
    fetchAllOrders({ commit, dispatch, rootGetters }, { options, searchQuery, filterData }) {
      commit('FETCH_REQUEST');

      const moreFieldThanPriceAndDate = 2;

      return OrdersApiService.getAll(builder => {
        builder.whereIn(['payment_method_id'], rootGetters['paymentMethods/getPaymentMethodsIds']);

        if (
          filterData &&
          (Object.keys(filterData).length > moreFieldThanPriceAndDate ||
            Object.keys(filterData.price).length ||
            Object.keys(filterData.createdAt).length)
        ) {
          if (filterData.id && !Number.isNaN(parseInt(filterData.id.trim(), 10))) {
            builder.whereEquals(['id'], Number(filterData.id.trim()));
          }
          if (filterData.customerName) {
            builder.whereContains(['customer_name'], filterData.customerName.trim());
          }
          if (filterData.companyName) {
            builder.whereContains(['company_name'], filterData.companyName.trim());
          }
          if (filterData.email) {
            builder.whereContains(['email'], filterData.email.trim());
          }
          if (filterData.phone) {
            builder.whereContains(['phone'], filterData.phone.trim().split(' ').join(''));
          }
          if (filterData.createdAt) {
            if (filterData.createdAt.from) {
              builder.whereGreaterOrEqual(['created_at'], filterData.createdAt.from.trim());
            }
            if (filterData.createdAt.to) {
              builder.whereLessOrEqual(['created_at'], filterData.createdAt.to.trim());
            }
          }
          if (filterData.price) {
            if (filterData.price.from && filterData.price.from >= 0) {
              builder.whereGreaterOrEqual(['taxed_price'], filterData.price.from);
            }
            if (filterData.price.to) {
              builder.whereLessOrEqual(['taxed_price'], filterData.price.to);
            }
          }
          if (filterData.countries && filterData.countries.length > 0) {
            builder.whereIn(['country'], filterData.countries);
          }
          if (filterData.customerGroups && filterData.customerGroups.length > 0) {
            builder.whereIn(['customer_group'], filterData.customerGroups);
          }
          if (filterData.shippingMethods && filterData.shippingMethods.length > 0) {
            builder.whereIn(['shipping_method_id'], filterData.shippingMethods);
          }
          if (filterData.shippingOptions && filterData.shippingOptions.length > 0) {
            builder.whereIn(
              ['shipping_option_id'],
              filterData.shippingOptions.map(id => Number(id))
            );
          }
          if (filterData.paymentMethods && filterData.paymentMethods.length > 0) {
            builder.whereIn(['payment_method_id'], filterData.paymentMethods);
          }
          if (filterData.orderStates && filterData.orderStates.length > 0) {
            builder.whereIn(['order_state_id'], filterData.orderStates);
          }
          if (filterData.brandId) {
            builder.whereEquals(['product_brand_id'], filterData.brandId);
          }
          if (filterData.productName && filterData.productName.length > 0) {
            builder.whereContains(['product_name'], filterData.productName);
          }
          if (filterData.productSKU && filterData.productSKU.length > 0) {
            builder.whereEquals(['product_sku'], filterData.productSKU);
          }
          if (filterData.productEan) {
            builder.whereEquals(['product_ean'], filterData.productEan.trim());
          }
        } else if (searchQuery) {
          if (Number.isNaN(parseInt(searchQuery.trim(), 10))) {
            builder.whereContains(['customer_name'], searchQuery.trim());
          } else {
            builder.whereEquals(['id', 'customer_id'], Number(searchQuery.trim()));
          }
        } else {
          builder.whereEquals([OrderModel.ATTR_ORDER_STATE_ID], ORDER_STATE.GETTING_READY);
        }

        if (options) {
          builder.setPage(options.page);
          builder.setPerPage(options.itemsPerPage);
          if (options.sortDesc && options.sortDesc[0]) {
            builder.sortBy(options.sortBy);
          } else if (options.sortBy && options.sortBy.length) {
            builder.sortByDesc(options.sortBy);
          } else {
            builder.sortByDesc([OrderModel.ATTR_CREATED_AT]);
          }
        }

        builder.embed([
          OrderModel.EMBED_STATE,
          OrderModel.EMBED_CUSTOMER_NAME,
          OrderModel.EMBED_SHIPPING,
          OrderModel.EMBED_PAYMENT,
          OrderModel.EMBED_INVOICES,
          OrderModel.EMBED_TAXED_ITEMS_PRICE,
          OrderModel.EMBED_TAX_AMOUNT,
          OrderModel.EMBED_ITEMS_COUNT,
        ]);
      })
        .then(response => {
          commit('SET_ITEMS', response.getItems());
          commit('SET_TOTAL', response.getMeta().records);
          commit('FETCH_SUCCESS');
        })
        .catch(response => {
          dispatch('handleError', response);
        });
    },

    fetchMaxOrders({ commit, dispatch }, { options, searchQuery, filterData }) {
      commit('FETCH_REQUEST');

      return OrdersApiService.getAll(builder => {
        if (searchQuery) {
          builder.whereEquals(['id'], Number(searchQuery.trim()));
        }

        if (filterData && filterData.orderStates && filterData.orderStates.length > 0) {
          builder.whereIn(['order_state_id'], filterData.orderStates);
        }

        if (options) {
          builder.setPage(options.page);
          builder.setPerPage(options.itemsPerPage);
          if (options.sortDesc && options.sortDesc[0]) {
            builder.sortBy(options.sortBy);
          } else if (options.sortBy && options.sortBy.length) {
            builder.sortByDesc(options.sortBy);
          } else {
            builder.sortByDesc([OrderModel.ATTR_CREATED_AT]);
          }
        }

        builder.embed([OrderModel.EMBED_ITEMS_COUNT]);
      })
        .then(response => {
          commit('SET_ITEMS', response.getItems());
          commit('SET_TOTAL', response.getMeta().records);
          commit('FETCH_SUCCESS');
        })
        .catch(response => {
          dispatch('handleError', response);
        });
    },

    fetchOrder({ commit, dispatch }, id) {
      commit('FETCH_REQUEST');

      return OrdersApiService.get(id, builder => {
        builder.embed([
          OrderModel.EMBED_STATE,
          OrderModel.EMBED_CUSTOMER_NAME,
          OrderModel.EMBED_SHIPPING,
          OrderModel.EMBED_PAYMENT,
          OrderModel.EMBED_INVOICES,
          OrderModel.EMBED_TAXED_ITEMS_PRICE,
          OrderModel.EMBED_TAX_AMOUNT,
        ]);
      })
        .then(response => {
          commit('SET_ITEM', response.getItem());
          commit('FETCH_SUCCESS');

          dispatch('fetchOrderItems', id);
          dispatch('fetchOrderCustomerAccounts', id);
        })
        .catch(response => {
          dispatch('handleError', response);
          return Promise.reject(response);
        });
    },

    updateOrder({ commit, dispatch }, { id, formData }) {
      commit('FETCH_REQUEST');

      return OrdersApiService.patch(id, formData)
        .then(response => {
          commit('FETCH_SUCCESS');
          commit('SET_ITEM', response.getItem());
          return response.getItem();
        })
        .catch(response => {
          dispatch('handleError', response);
          return Promise.reject();
        });
    },

    fetchOrderItemsWithoutImages({ commit, dispatch }, id) {
      commit('FETCH_REQUEST');

      return OrderItemsApiService.getAll(id, builder => {
        builder.sortBy(OrderItemModel.ATTR_SKU);
        builder.embed([OrderItemModel.EMBED_VARIATION_PROPERTIES_NAMES, OrderItemModel.EMBED_PRODUCT_IMAGE_URL]);
      })
        .then(response => {
          const items = response.getItems();
          commit('SET_ALL_ITEMS', { orderItems: items, orderId: id });
          commit('SET_ORDER_ITEMS', { orderId: id, items });
          commit('FETCH_SUCCESS');
        })
        .catch(response => {
          dispatch('handleError', response);
        });
    },

    fetchOrderItems({ commit, dispatch }, id) {
      commit('FETCH_REQUEST');

      return OrderItemsApiService.getAll(id, builder => {
        builder.sortBy(OrderItemModel.ATTR_SKU);
        builder.embed([OrderItemModel.EMBED_VARIATION_PROPERTIES_NAMES, OrderItemModel.EMBED_PRODUCT_IMAGE_URL]);
      })
        .then(response => {
          const items = response.getItems();
          commit('SET_ALL_ITEMS', { orderItems: items, orderId: id });
          commit('SET_ORDER_ITEMS', { orderId: id, items });
          commit('FETCH_SUCCESS');
        })
        .catch(response => {
          dispatch('handleError', response);
        });
    },

    clearStore({ commit }) {
      commit('CLEAR_ITEMS');
    },

    fetchOrderCustomerAccounts({ commit, dispatch }, id) {
      commit('FETCH_REQUEST');

      return OrderCustomerAccountsApiService.getAll(id)
        .then(response => {
          commit('SET_ORDER_CUSTOMER_ACCOUNTS', { orderId: id, items: response.getItems() });
          commit('FETCH_SUCCESS');
        })
        .catch(response => {
          dispatch('handleError', response);
        });
    },

    fetchOrdersSearchingRemaining({ commit, dispatch }, { idData }) {
      return OrdersSearchingRemainingApiService.post(idData)
        .then(response => {
          commit('SET_REMAINING', response.getItem());
        })
        .catch(error => {
          dispatch('handleError', error);
        });
    },

    fetchOrdersPackagingRemaining({ commit, dispatch }, { idData }) {
      return OrdersPackagingRemainingApiService.post(idData)
        .then(response => {
          commit('SET_REMAINING', response.getItem());
        })
        .catch(error => {
          dispatch('handleError', error);
        });
    },

    setLastChange({ commit }, payload) {
      commit('SET_LAST_ORDER_CHANGE', payload);
    },

    handleError({ commit }, response) {
      commit('FETCH_FAIL');
      const message = response.getFirstValidationErrorMessage() || response.getMessage();
      this.$app.$alertError(message);
    },
  },
};
